import { PAGES, GAME_TYPES } from "@/common/consts";

import DynamicPage from "@/pages/dynamic-page"; // Avoid code splitting in this page.
import { pageRoutes as templateGamePageRoutes } from "./new-game-pages";
import { devPages } from "./dev-pages";

import ResultsPageSwitcher from "@/components/results-page-switcher";

const coreRoutes = [
	...devPages,
	...templateGamePageRoutes,
	{
		path: "/app/about/",
		name: PAGES.APP_ABOUT,
		component: () =>
			import(/* webpackChunkName: "about-app" */ "@/pages/about-app"),
		meta: {
			breadcrumb: [{ label: "About Word Finder" }],
		},
	},
	{
		path: "/app/privacy-policy/",
		name: PAGES.APP_PRIVACY_POLICY,
		component: () =>
			import(/* webpackChunkName: "legal-info" */ "@/pages/legal-info"),
		meta: {
			breadcrumb: [{ label: "APP Privacy Policy" }],
		},
	},
	{
		path: "/scrabble-dictionary/:tiles",
		name: PAGES.SCRABBLE_DICT_RESULTS,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Scrabble Dictionary", url: "/scrabble-dictionary/" },
				{ label: "Results" },
			],
			gameType: GAME_TYPES.UNSCRAMBLE,
		},
	},
	{
		path: "/unscramble/:tiles",
		name: PAGES.UNSCRAMBLER_RESULTS,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Unscramble", url: "/unscramble/" },
				{ label: "Results" },
			],
			gameType: GAME_TYPES.UNSCRAMBLE,
		},
	},
	{
		path: "/anagram-solver/:tiles",
		name: PAGES.ANAGRAM_RESULTS,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{
					label: "Anagram Solver",
					url: "/anagram-solver/",
				},
				{ label: "Results" },
			],
			gameType: GAME_TYPES.UNSCRAMBLE,
		},
	},
	{
		path: "/wordle/results",
		name: PAGES.WORDLE_RESULTS,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Wordle Solver", url: "/wordle/" },
				{ label: "Results" },
			],
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/wordle/answers/",
		name: PAGES.WORDLE_ANSWERS,
		component: () =>
			import(
				/* webpackChunkName: "wordle-answers" */ "@/pages/wordle/answers"
			),
		meta: {
			breadcrumb: [
				{ label: "Wordle Solver", url: "/wordle/" },
				{ label: "List of Wordle Answers" },
			],
		},
	},
	{
		path: "/wordle/hints/",
		name: PAGES.WORDLE_HINTS,
		component: () =>
			import(
				/* webpackChunkName: "wordle-hints" */ "@/pages/wordle/hints"
			),
		meta: {
			breadcrumb: [
				{ label: "Wordle Solver", url: "/wordle/" },
				{ label: "Wordle Hints" },
			],
		},
	},
	{
		path: "/quordle/results",
		name: PAGES.QUORDLE_RESULTS,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Quordle Solver", url: "/quordle/" },
				{ label: "Results" },
			],
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/letter-words/",
		name: PAGES.WORDS_BY_LENGTH_INDEX,
		component: () =>
			import(/* webpackChunkName: "words-lists" */ "@/pages/words-lists"),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
		},
	},
	{
		path: "/letter-words/:length/",
		name: PAGES.WORDS_BY_LENGTH_RESULTS,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			endpoint: "length",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/consonants/",
		name: PAGES.WORDS_CONSONANTS,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
				{ label: "Words Without Vowels" },
			],
			endpoint: "consonants",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/ending-with/",
		name: PAGES.WORDS_ENDING_INDEX,
		component: () =>
			import(/* webpackChunkName: "words-lists" */ "@/pages/words-lists"),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
		},
	},
	{
		path: "/ending-in-ing/",
		name: PAGES.WORDS_ENDING_ING,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
				{ label: "Words Ending in ING" },
			],
			endpoint: "ing",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/ending-with/:letter/",
		name: PAGES.WORDS_ENDING_RESULTS,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			content: {
				pageTitle: "Words Ending in",
			},
			endpoint: "end-with",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/words-with-the-letter/",
		name: PAGES.WORDS_HAVING_INDEX,
		component: () =>
			import(/* webpackChunkName: "words-lists" */ "@/pages/words-lists"),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
		},
	},
	{
		path: "/words-with-the-letter/:letter/",
		name: PAGES.WORDS_HAVING_RESULTS,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			content: {
				pageTitle: "Words with the Letter",
			},
			endpoint: "words-with",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/index/",
		name: PAGES.WORDS_LISTS_INDEX,
		component: () =>
			import(
				/* webpackChunkName: "wordlistsindex" */ "@/pages/word-lists-index"
			),
		meta: {
			breadcrumb: [{ label: "Word Lists for Word Games" }],
		},
	},
	{
		path: "/words-that-start/",
		name: PAGES.WORDS_STARTING_INDEX,
		component: () =>
			import(/* webpackChunkName: "words-lists" */ "@/pages/words-lists"),
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
		},
	},
	{
		path: "/words-that-start/:letter/",
		name: PAGES.WORDS_STARTING_RESULTS,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
			],
			content: {
				pageTitle: "Words that start with",
			},
			endpoint: "start-with",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/vowels/",
		name: PAGES.WORDS_VOWELS,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
				{ label: "Vowels" },
			],
			endpoint: "vowels",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/q-no-u/",
		name: PAGES.WORDS_WITH_Q_NO_U,
		component: ResultsPageSwitcher,
		meta: {
			breadcrumb: [
				{ label: "Word Lists for Word Games", url: "/index/" },
				{ label: "Words with Q no U" },
			],
			endpoint: "q-no-u",
			gameType: GAME_TYPES.WORDSLISTS,
		},
	},
	{
		path: "/game-cheats/",
		name: PAGES.GAME_CHEATS,
		component: () =>
			import(/* webpackChunkName: "game-cheats" */ "@/pages/game-cheats"),
		meta: {
			breadcrumb: [{ label: "Game Cheats" }],
		},
	},
	{
		// Dynamic page:
		path: "/dynamic-page/",
		name: PAGES.DYNAMIC_PAGE,
		component: DynamicPage,
	},
	{
		// Dynamic results page
		path: "/dynamic-results-page/",
		name: PAGES.DYNAMIC_RESULTS_PAGE,
		component: () =>
			import(
				/* webpackChunkName: "results-en-new" */ "@/pages/results-en-new"
			),
	},
	{
		path: "/blog/",
		name: PAGES.ARTICLE_INDEX,
		component: () =>
			import(
				/* webpackChunkName: "article-index" */ "@/pages/articles/index"
			),
	},
	{
		path: "/blog/:slug",
		name: PAGES.ARTICLE_DETAIL,
		component: () =>
			import(
				/* webpackChunkName: "article-detail" */ "@/pages/articles/detail"
			),
	},
	{
		path: "/saved-words/",
		name: PAGES.SAVED_WORDS,
		component: () =>
			import(/* webpackChunkName: "saved-words" */ "@/pages/saved-words"),
	},
	{
		path: "/app/get-the-app/",
		name: PAGES.GET_THE_APP,
		component: () =>
			import(/* webpackChunkName: "get-the-app" */ "@/pages/get-app"),
		meta: {
			breadcrumb: [{ label: "Get The App" }],
		},
	},
	{
		path: "/about/",
		name: PAGES.ABOUT,
		component: () =>
			import(/* webpackChunkName: "about" */ "@/pages/about"),
		meta: {
			breadcrumb: [{ label: "About Us" }],
		},
	},
	{
		path: "/games-and-puzzles/",
		name: PAGES.GAMES_AND_PUZZLES,
		component: () =>
			import(
				/* webpackChunkName: "games-and-puzzles" */ "@/pages/games-and-puzzles"
			),
		meta: {
			breadcrumb: [{ label: "Games & Puzzles" }],
		},
	},
	{
		path: "/popular-pages/",
		name: PAGES.POPULAR_PAGES,
		component: () =>
			import(
				/* webpackChunkName: "single-cms-page" */ "@/pages/single-cms-page"
			),
		meta: {
			breadcrumb: [{ label: "Popular Pages" }],
		},
	},
	{
		path: "/top-searched-word-combos/",
		name: PAGES.TOP_SEARCHED,
		component: () =>
			import(
				/* webpackChunkName: "single-cms-page" */ "@/pages/single-cms-page"
			),
		meta: {
			breadcrumb: [{ label: "Top Searched Word Combos" }],
		},
	},
	{
		path: "/top-unscrambles/",
		name: PAGES.TOP_UNSCRAMBLES,
		component: () =>
			import(
				/* webpackChunkName: "single-cms-page" */ "@/pages/single-cms-page"
			),
		meta: {
			breadcrumb: [{ label: "Top Unscrambles" }],
		},
	},
	{
		path: "/ltk-control-room/",
		name: "ltk-control-room",
		component: () =>
			import(
				/* webpackChunkName: "control-room" */ "@/pages/control-room"
			),
	},
	{
		// Soft 404 Pages:
		path: "*",
		name: PAGES.NOT_FOUND,
		component: () =>
			import(
				/* webpackChunkName: "page-not-found" */ "@/pages/page-not-found"
			),
	},
];

const redirections = [
	{
		path: "/app",
		redirect: {
			name: PAGES.APP_ABOUT,
		},
	},
	{
		path: "/q_no_u",
		redirect: {
			name: PAGES.WORDS_WITH_Q_NO_U,
		},
	},
	{
		path: "/wordcookies",
		redirect: {
			name: PAGES.WORDCOOKIES,
		},
	},
	{
		// [WFW-286] Kept for backwards compatibility:
		path: "/unscramble/remember_tiles/:tiles",
		redirect: {
			name: PAGES.UNSCRAMBLER_RESULTS,
		},
	},
	{
		// [WFW-286] Kept for backwards compatibility:
		path: "/scrabble-dictionary/exact-match/:tiles",
		redirect: {
			name: PAGES.SCRABBLE_DICT_RESULTS,
		},
	},
	{
		path: "/4pics1word/:wordlength/:tiles",
		redirect: {
			name: PAGES.FOURPICS,
		},
	},
	{
		path: "/wordle/:tiles",
		redirect: (to) => {
			let includes = null,
				excludes = null;
			const tiles = to.params?.tiles || "";
			// If there are no valid tiles in the results, fall back to the landing page
			if (!tiles) {
				return {
					name: PAGES.WORDLE,
				};
			}

			const position = tiles.indexOf("-");
			switch (position) {
				case -1:
					includes = to.params.tiles;
					break;
				case 0:
					excludes = to.params.tiles.substr(1);
					break;
				default:
					[includes, excludes] = to.params.tiles.split("-");
					break;
			}

			const query = {};
			if (includes) query.includes = includes;
			if (excludes) query.excludes = excludes;

			return {
				path: "/wordle/results/",
				query,
			};
		},
	},
	{
		path: "/feature-toggling/",
		redirect: "/ltk-control-room/",
	},
];

// List of routes (sorted)
export const routes = coreRoutes.concat(redirections);

// Map of routes accessible by name (this is meant for unit testing purposes where it's useful to be able to pick routes by its name)
export const routesMap = coreRoutes.reduce(function(map, route) {
	map[route.name] = route;
	return map;
}, {});
